import React, { useEffect, useId, useRef } from 'react';
import { IconClose } from '../icons';
import useFocusTrap from '../hooks/useFocusTrap';

// Right-hand slide-over used for all create/edit forms. `icon` renders inside
// a brand-tinted square next to the title; `footer` is pinned below the
// scrollable body.
const Drawer = ({ isOpen, onClose, title, icon, footer, width = 524, closeDisabled, children }) => {
  const panelRef = useRef(null);
  const titleId = useId();

  // Don't let the page scroll behind the drawer. The previous value is restored
  // rather than cleared: the scroll may already be locked by an overlay this
  // drawer was opened from.
  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  // Escape + Tab cycling inside the panel. The stack inside the hook keeps
  // nested overlays apart: Escape only closes the topmost one.
  // closeDisabled means a request is in flight, so Escape must not fire either.
  useFocusTrap(panelRef, { isOpen, onEscape: closeDisabled ? undefined : onClose });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className="absolute inset-0 bg-overlay"
        onClick={closeDisabled ? undefined : onClose}
      />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        // Link the heading when it is plain text; otherwise the dialog would be
        // nameless to a screen reader.
        aria-labelledby={typeof title === 'string' && title ? titleId : undefined}
        aria-label={typeof title === 'string' && title ? undefined : 'Dialog'}
        className="relative max-w-full h-full bg-card flex flex-col shadow-dropdown"
        style={{ width }}
      >
        <div className="shrink-0 px-4 pt-4 pb-4 flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {icon && (
              <span className="w-11 h-11 rounded-lg bg-brand-50 flex items-center justify-center shrink-0 text-brand-500">
                {icon}
              </span>
            )}
            <h3 id={titleId} className="text-[18px] leading-[22px] font-medium text-ink truncate">
              {title}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={closeDisabled}
            aria-label="Close"
            // 44x44 on touch — the same idiom as rowActionStyles.
            className="w-6 h-6 max-md:w-11 max-md:h-11 inline-flex items-center justify-center border border-line rounded-[4px] text-ink-muted hover:text-ink disabled:text-ink-disabled disabled:cursor-not-allowed shrink-0"
          >
            <IconClose size={14} />
          </button>
        </div>
        <div className="h-px bg-line w-full shrink-0" />

        <div className="flex-1 overflow-y-auto">{children}</div>

        {footer && (
          <div className="shrink-0 border-t border-line bg-card p-4 flex gap-3.5 justify-end">
            {footer}
          </div>
        )}
      </aside>
    </div>
  );
};

// Shared input styling for every form field inside a Drawer.
export const inputClass =
  'w-full border border-line rounded-lg p-3 text-sm text-ink placeholder:text-ink-placeholder focus:outline-none focus:border-brand-500 transition-colors disabled:bg-surface';

// Section wrapper: uppercase label + stacked fields.
export const DrawerSection = ({ title, children }) => (
  <section className="flex flex-col gap-4 px-4">
    {title && (
      <h4 className="text-xs font-medium uppercase tracking-[0.54px] text-ink-muted">{title}</h4>
    )}
    {children}
  </section>
);

export const DrawerDivider = () => <div className="h-px bg-line w-full" />;

// A <label> that isn't tied to its control is read as loose text by a screen
// reader, and clicking it doesn't focus the field. The id is taken from the
// control itself when it has one (every caller here sets it), generated only as
// a fallback and then pushed onto the child — so existing call sites keep
// working unchanged either way.
export const Field = ({ label, hint, htmlFor, children }) => {
  const fallbackId = useId();
  const child = React.isValidElement(children) ? children : null;
  const id = htmlFor || child?.props?.id || fallbackId;
  const control = child && !child.props.id ? React.cloneElement(child, { id }) : children;

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={id} className="text-sm text-ink-muted">
          {label}
        </label>
      )}
      {control}
      {hint && <p className="text-xs text-ink-muted">{hint}</p>}
    </div>
  );
};

export default Drawer;
